import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { QuickAccessLinkDetails, NetworkDetails } from './HomePage';

const useStyles = makeStyles((theme) => ({
  drawer: {
    '& .MuiDrawer-paper': {
      width: 400,
      padding: theme.spacing(3),
      marginLeft: theme.spacing(2),
      borderRadius: '16px 0 0 16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
      [theme.breakpoints.down('sm')]: {
        width: '100vw',
        marginLeft: 0,
        borderRadius: 0,
      },
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
  },
  section: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.main,
    fontSize: '1.1rem',
    borderBottom: `2px solid ${theme.palette.primary.light}`,
    paddingBottom: theme.spacing(0.5),
    backgroundColor: 'transparent',
    textShadow: '0 0 1px rgba(0,0,0,0.1)',
  },
  detailItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5),
    border: `2px solid ${theme.palette.grey[400]}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
  },
  detailLabel: {
    fontWeight: 700,
    color: theme.palette.primary.dark,
    minWidth: 120,
    display: 'inline-block',
    fontSize: '0.9rem',
  },
  detailValue: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    fontSize: '0.9rem',
  },
  statusChip: {
    marginTop: theme.spacing(0.5),
  },
  linkButton: {
    marginTop: theme.spacing(2),
  },
  contactSection: {
    backgroundColor: theme.palette.primary.light,
    padding: theme.spacing(2.5),
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    border: `2px solid ${theme.palette.primary.main}`,
    '& .MuiTypography-root': {
      color: theme.palette.primary.contrastText,
    },
  },
  contactSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
    fontSize: '1.1rem',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(0.5),
    backgroundColor: 'transparent',
    textShadow: '0 0 1px rgba(0,0,0,0.1)',
  },
  contactDetailItem: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5),
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
  },
  contactDetailLabel: {
    fontWeight: 700,
    color: theme.palette.primary.dark,
    minWidth: 120,
    display: 'inline-block',
    fontSize: '0.9rem',
  },
  contactDetailValue: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginLeft: theme.spacing(1),
    fontSize: '0.9rem',
  },
}));

interface DetailsSidePanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  url: string;
  iconUrl: string;
  details?: QuickAccessLinkDetails;
}

export const DetailsSidePanel: React.FC<DetailsSidePanelProps> = ({
  open,
  onClose,
  title,
  url,
  iconUrl,
  details,
}) => {
  const classes = useStyles();

  const handleOpenLink = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderDetailItem = (label: string, value: string | undefined) => {
    return (
      <Box className={classes.detailItem}>
        <span className={classes.detailLabel}>{label}:</span>
        <span className={classes.detailValue}>{value || 'Not Available'}</span>
      </Box>
    );
  };

  const renderContactDetailItem = (label: string, value: string | undefined) => {
    return (
      <Box className={classes.contactDetailItem}>
        <span className={classes.contactDetailLabel}>{label}:</span>
        <span className={classes.contactDetailValue}>{value || 'Not Available'}</span>
      </Box>
    );
  };

  const renderNetworkDetails = (networkDetails: NetworkDetails | undefined) => {
    return (
      <Box className={classes.section}>
        <Typography className={classes.sectionTitle}>Network Details</Typography>
        {renderDetailItem('Master Node', networkDetails?.masterNode)}
        {renderDetailItem('Worker Nodes', networkDetails?.workerNodes)}
        {renderDetailItem('Subnet', networkDetails?.subnet)}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={classes.drawer}
    >
      <Box>
        <Box className={classes.header}>
          <Box display="flex" alignItems="center">
            <img
              src={iconUrl}
              alt={title}
              style={{ height: 32, width: 32, marginRight: 12, borderRadius: 4 }}
            />
            <Typography className={classes.title}>{title}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Description</Typography>
          <Typography variant="body2" color="textSecondary">
            {details?.description || 'Not Available'}
          </Typography>
        </Box>

        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Service Information</Typography>
          {renderDetailItem('Service', details?.service)}
          {renderDetailItem('Version', details?.version)}
          {renderDetailItem('URL', details?.url || url)}
        </Box>

        {renderNetworkDetails(details?.networkDetails)}

        <Box className={classes.contactSection}>
          <Typography className={classes.contactSectionTitle}>Contact Information</Typography>
          {renderContactDetailItem('Contact', details?.contact)}
        </Box>

        <Box className={classes.section}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenLink}
            className={classes.linkButton}
          >
            Open {title}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};